import {
    AlphaAttribute,
    ColorAttribute,
    getAttributesToSerialize,
    NumberAttribute,
    StringAttribute,
    StringOrNumberAttribute
} from '../../attributes';
import type { CanvasTextDynamicBoxRendererSettings } from '../renderer/CanvasTextDynamicBox';
import { Config } from '../../../video-data/config';
import type {
    DynamicHeight,
    DynamicWidth } from '../../../enums';
import {
    DynamicInferredHeight,
    DynamicInferredWidth,
    DynamicXDirection,
    DynamicYDirection
} from '../../../enums';
import { CanvasTextDynamicBoxRenderer } from '../renderer/CanvasTextDynamicBox';
import { int2rgba } from '../../../libs/videoData';
import { CanvasTextAssetDynamicGraphic } from './CanvasTextAssetDynamicGraphic';

/**
 * CanvasTextAssetDynamicBox is the Luminary equivalent of CanvasTextDynamicBox and provides a bridge between Luminary
 * and the CanvasText library.
 */
export class CanvasTextAssetDynamicBox extends CanvasTextAssetDynamicGraphic {
    override _classType = 'CanvasTextDynamicBox';

    override readonly type = Config.BOX_GRAPHIC;
    @StringOrNumberAttribute({ choices: Object.values(DynamicInferredWidth) })
    boxWidth: DynamicWidth = DynamicInferredWidth.width;
    @StringOrNumberAttribute({ choices: Object.values(DynamicInferredHeight) })
    boxHeight: DynamicHeight = DynamicInferredHeight.maxHeight;
    @NumberAttribute({ min: 0, max: Infinity })
    topLeftRadius = 0;
    @NumberAttribute({ min: 0, max: Infinity })
    topRightRadius = 0;
    @NumberAttribute({ min: 0, max: Infinity })
    bottomLeftRadius = 0;
    @NumberAttribute({ min: 0, max: Infinity })
    bottomRightRadius = 0;
    @NumberAttribute()
    paddingTop = 0;
    @NumberAttribute()
    paddingRight = 0;
    @NumberAttribute()
    paddingBottom = 0;
    @NumberAttribute()
    paddingLeft = 0;
    @StringAttribute({ choices: Object.values(DynamicXDirection) })
    xDirection = DynamicXDirection.right;
    @StringAttribute({ choices: Object.values(DynamicYDirection) })
    yDirection = DynamicYDirection.bottom;
    @NumberAttribute()
    xProgress = 1.0;
    @NumberAttribute()
    yProgress = 1.0;
    @ColorAttribute()
    public strokeColor = 0xFFFFFF;
    @AlphaAttribute()
    public strokeAlpha = 1;
    @NumberAttribute({ min: 0, animatable: false })
    public strokeWidth = 0;

    override getCanvasRendererSettings(): CanvasTextDynamicBoxRendererSettings {
        const base = super.getCanvasRendererSettings();

        const boxWidth = typeof this.boxWidth === 'number' ? this.boxWidth * this.referenceToOutputMultiplier : this.boxWidth;
        const boxHeight = typeof this.boxHeight === 'number' ? this.boxHeight * this.referenceToOutputMultiplier : this.boxHeight;
        const strokeStyle = int2rgba(this.strokeColor, this.strokeAlpha);

        return {
            ...base,
            type: this.type,
            boxWidth,
            boxHeight,
            topLeftRadius: this.topLeftRadius * this.referenceToOutputMultiplier,
            topRightRadius: this.topRightRadius * this.referenceToOutputMultiplier,
            bottomLeftRadius: this.bottomLeftRadius * this.referenceToOutputMultiplier,
            bottomRightRadius: this.bottomRightRadius * this.referenceToOutputMultiplier,
            paddingTop: this.paddingTop * this.referenceToOutputMultiplier,
            paddingRight: this.paddingRight * this.referenceToOutputMultiplier,
            paddingBottom: this.paddingBottom * this.referenceToOutputMultiplier,
            paddingLeft: this.paddingLeft * this.referenceToOutputMultiplier,
            strokeStyle,
            strokeWidth: this.strokeWidth * this.referenceToOutputMultiplier,
            xDirection: this.xDirection,
            yDirection: this.yDirection,
            xProgress: this.xProgress,
            yProgress: this.yProgress,
        };
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(getAttributesToSerialize(this));
    }

    /* The list of properties that can be animated during draw */
    override get animatableProperties() {
        // Since CanvasTextDynamicBox is responsible for rendering our text we need to ask it which properties it
        // supports. See its rendering logic for more details.
        return CanvasTextDynamicBoxRenderer.animatableProperties;
    }

    /** @inheritDoc */
    override get animatableGeometricProperties() {
        return CanvasTextDynamicBoxRenderer.animatableGeometricProperties;
    }

    override loadTextAssetStyle(settings) {
        // Our old box graphics had two sets of properties that did the same thing. Scale & Progress animations
        // had the same effect, but the canvas renderer only supports Progress. So we need to remap scale.x
        // to xProgress (and the same for y).
        settings.animation.forEach(animation => {
            if (animation.property === 'scale.x') {
                animation.property = 'xProgress';
            }
            if (animation.property === 'scale.y') {
                animation.property = 'yProgress';
            }
        });
        super.loadTextAssetStyle(settings);

        this.boxWidth = settings.shape.boxWidth;
        this.boxHeight = settings.shape.boxHeight;
        this.topLeftRadius = settings.shape.topLeftRadius;
        this.topRightRadius = settings.shape.topRightRadius;
        this.bottomLeftRadius = settings.shape.bottomLeftRadius;
        this.bottomRightRadius = settings.shape.bottomRightRadius;
        this.paddingTop = settings.shape.paddingTop;
        this.paddingRight = settings.shape.paddingRight;
        this.paddingBottom = settings.shape.paddingBottom;
        this.paddingLeft = settings.shape.paddingLeft;
        this.strokeAlpha = settings.shape.strokeAlpha;
        this.strokeColor = settings.shape.strokeColor;
        this.strokeWidth = settings.shape.strokeWidth;
        this.xDirection = settings.shape.horizontalDirection;
        this.yDirection = settings.shape.verticalDirection;
        this.xProgress = settings.shape.progressXValue;
        this.yProgress = settings.shape.progressYValue;
    }
}
